/**
 * UI acceptance testing — the Testing screen's whole API
 * (`backend/app/routers/ui_testing.py`, prefix `/api/ui-testing`).
 *
 * Cases, runs, evidence, the defect register and the test-data ledger for one
 * application. Reading needs READ on the project; running, editing and every
 * defect decision need TEST. Plan: docs/plans/ui-acceptance-testing.md.
 */

import api from './client'
import { APP_BASE } from '@/config/basePath'
import type { UnitTestReport } from './unitTestAgent'

const base = (project: string) => `/ui-testing/${encodeURIComponent(project)}`

// ── shapes ────────────────────────────────────────────────────────────────

export type CaseStatus = 'passed' | 'failed' | 'blocked' | 'skipped'
export type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'interrupted'
export type DefectStatus = 'open' | 'logged' | 'closed'
export type DefectClassification = 'product' | 'test' | 'environment' | 'unclassified'
export type Severity = 'critical' | 'high' | 'medium' | 'low'

export interface Persona { roles: string[] }

export interface TestPlan {
  version?: number
  scope?: { surfaces?: string[] }
  personas: Record<string, Persona>
  data_policy?: { keep_test_data?: boolean }
  error?: string
  [k: string]: unknown
}

export interface Issue {
  case: string
  where: string
  message: string
  severity: 'error' | 'warning'
}

export interface CoverageSummary {
  frs_found: boolean
  sections: number
  sections_covered: number
  rules: number
  rules_covered: number
}

/** One FRS section in the coverage matrix, with the cases that cite it. */
export interface CoverageSection {
  id: string
  key: string
  title: string
  module: string
  object: string
  description?: string
  cases: string[]
}

/** One business rule of a section. `section_key` is its section's `key`. */
export interface CoverageRule {
  id: string
  key: string
  section: string
  section_key?: string
  rule: string
  cases: string[]
}

export interface Coverage extends CoverageSummary {
  uncovered_sections: CoverageSection[]
  uncovered_rules: CoverageRule[]
  matrix: CoverageSection[]
  rule_matrix?: CoverageRule[]
  complete: boolean
}

export interface RunSummary {
  run_id: string
  status: RunStatus
  started_at?: string
  finished_at?: string
  total?: number
  done?: number
  counts?: Partial<Record<CaseStatus, number>>
  started_by?: string
  /** Which browser carried the cases out: the server's own (`playwright`) or
   *  someone's own Chrome, driven from Claude Code (`chrome`). */
  driver?: RunDriver
}

export type RunDriver = 'playwright' | 'chrome'

/** A browser-driven run's tab (backend `ui_testing/browser_run.py`). */
export interface BrowserSession {
  state?: 'waiting' | 'connected' | 'finished'
  user_agent?: string
  connected_at?: string
  persona_origin?: string | null
  attach_by_s?: number
}

/** One action the tab could not do by script, waiting to be done by hand. */
export interface NeedsHand {
  seq: number
  op?: string
  reason?: string
  selector?: string
  value?: string
  url?: string
  since?: string
  hint?: string
}

export interface LedgerSummary {
  runs: Record<string, { kept: number; cleaned: number; gone: number }>
  kept: number
}

export interface Overview {
  plan: TestPlan
  cases: number
  cases_by_origin: Record<string, number>
  unreadable_cases: Issue[]
  coverage: CoverageSummary | null
  runs: RunSummary[]
  defects: Record<DefectStatus, number>
  test_data: LedgerSummary
  can_test: boolean
  /** A developer / project manager who may fix a failed case in place (Ask
   *  AI) — quality control only raises tickets. */
  can_fix?: boolean
}

export interface CaseRow {
  id: string
  title?: string
  object?: string
  surface?: string
  type?: string
  priority?: string
  persona?: string
  origin?: string
  requirement?: string[]
  errors: number
}

export type TestCase = Record<string, unknown> & { id: string }

export interface SeedResult {
  written: string[]
  unchanged: string[]
  kept_edited: string[]
  removed: string[]
  skipped: string[]
  total_seeded: number
}

export interface ValidateResult {
  ok: boolean
  cases: number
  errors: number
  warnings: number
  issues: Issue[]
}

export interface RunCaseSummary {
  id: string
  title?: string
  object?: string
  surface?: string
  type?: string
  priority?: string
  persona?: string
  status: CaseStatus
  failure?: { step: number; code?: string; message?: string } | null
  duration_s?: number
  requirement?: string[]
}

export interface RunDetail extends RunSummary {
  project?: string
  /** Report files already built for this run (relative to the run folder). */
  report?: { docx?: string; html?: string } | null
  options?: Record<string, unknown>
  skipped_invalid?: string[]
  cases: RunCaseSummary[]
  environment?: Record<string, unknown>
  browser?: string
  error?: string
  warnings?: string[]
  data_ledger?: { kept?: number; cleaned?: number; gone?: number }
  browser_session?: BrowserSession
  needs_agent?: NeedsHand | null
}

export interface StepResult {
  n: number
  kind: 'precondition' | 'step'
  step: Record<string, unknown>
  status: CaseStatus
  result?: string
  error?: string
  code?: string
  detail?: unknown
  duration_s?: number
  screenshot?: string
  /** Done by hand in the tab (a browser-driven run). */
  by_hand?: boolean
}

export interface CaseResult extends RunCaseSummary {
  steps: StepResult[]
  /** A Playwright trace.zip was kept for this case. */
  trace?: boolean
  started_at?: string
  finished_at?: string
  created_records?: { obj: string; txn_id: string }[]
  blocked_navigations?: string[]
  native_dialogs?: { type: string; text: string }[]
  server_log?: string
  driver?: RunDriver
  /** `rendered`: drawn by the page itself (html2canvas), not a pixel capture. */
  screenshot_kind?: 'pixels' | 'rendered'
  agent_steps?: number[]
}

export interface StartRunBody {
  case_ids?: string[]
  objects?: string[]
  surfaces?: string[]
  personas?: string[]
  trace?: 'failed' | 'all' | 'none'
}

export interface ReportResult {
  html: string
  docx: string
  cases: number
  defects: number
  pass_rate: number
}

export interface Defect {
  id: number
  number: number
  ref: string
  origin: 'run' | 'manual'
  case_id: string | null
  title: string
  severity: Severity
  classification: DefectClassification
  status: DefectStatus
  expected: string | null
  actual: string | null
  repro: string | null
  suspected_object: string | null
  rationale: string | null
  requirement: string[]
  evidence: {
    run_id?: string
    case_dir?: string
    screenshots?: string[]
    trace?: boolean
    failure_code?: string
    step?: number
  }
  first_run_id: string | null
  last_run_id: string | null
  occurrences: number
  retest_passed_run_id: string | null
  support_issue: number | null
  support_issue_id: number | null
  support_status: string | null
  /** How it was routed (sent to Support on its own, or waiting for a person) and the decision. */
  review: DefectReview | null
  logged_by: string | null
  logged_at: string | null
  closed_reason: string | null
  closed_by: string | null
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface LedgerEntry {
  obj: string
  txn_id: string
  run_id: string
  case: string
  via: string
  created_at: string
  fingerprint: string | null
  status: 'kept' | 'cleaned' | 'gone'
}

export interface CleanResult {
  deleted: number
  already_gone: number
  changed_since_test: { obj: string; txn_id: string; case: string }[]
  failed: { obj: string; txn_id: string; error: string }[]
}

// ── calls ─────────────────────────────────────────────────────────────────

export async function getOverview(project: string): Promise<Overview> {
  return (await api.get<Overview>(`${base(project)}/overview`)).data
}

export async function savePlan(project: string, plan: TestPlan): Promise<void> {
  await api.put(`${base(project)}/plan`, { plan })
}

export async function listCases(project: string): Promise<{ cases: CaseRow[]; unreadable: Issue[] }> {
  return (await api.get(`${base(project)}/cases`)).data
}

export async function getCase(project: string, caseId: string): Promise<TestCase> {
  return (await api.get<TestCase>(`${base(project)}/cases/${encodeURIComponent(caseId)}`)).data
}

export async function saveCase(project: string, caseId: string, testCase: TestCase):
  Promise<{ ok: boolean; warnings: Issue[] }> {
  return (await api.put(`${base(project)}/cases/${encodeURIComponent(caseId)}`, { case: testCase })).data
}

/** One field of a screen's form, as a hand-written case may name it. */
export interface BuilderColumn {
  key: string
  db_name: string
  heading: string
  type: string
  mandatory: boolean
  enterable: boolean
  hidden: boolean
  lookup: boolean
  choices: string[]
}

/** What "Add a test" may use for one screen (`GET /case-builder?obj=`). */
export interface CaseBuilderVocabulary {
  obj: string
  obj_type: string
  title: string
  forms: { form_no: string; title: string; parent_form_no: string; columns: BuilderColumn[] }[]
  actions: { name: string; form_no: string; handler: string }[]
  builtin_actions: string[]
  message_kinds: string[]
  field_states: string[]
  case_types: string[]
  priorities: string[]
  tokens: string[]
  steps: string[]
  expects: string[]
  personas: string[]
  next_id: string
}

export async function getCaseBuilder(project: string, obj: string): Promise<CaseBuilderVocabulary> {
  return (await api.get<CaseBuilderVocabulary>(`${base(project)}/case-builder`, { params: { obj } })).data
}

export async function deleteCase(project: string, caseId: string): Promise<void> {
  await api.delete(`${base(project)}/cases/${encodeURIComponent(caseId)}`)
}

export async function seedCases(project: string): Promise<SeedResult> {
  return (await api.post<SeedResult>(`${base(project)}/seed`)).data
}

export async function validateCases(project: string): Promise<ValidateResult> {
  return (await api.post<ValidateResult>(`${base(project)}/validate`)).data
}

export async function getCoverage(project: string): Promise<Coverage> {
  return (await api.get<Coverage>(`${base(project)}/coverage`)).data
}

export async function startRun(project: string, body: StartRunBody):
  Promise<{ run_id: string; cases: number; skipped_invalid: number }> {
  return (await api.post(`${base(project)}/runs`, body)).data
}

export async function listRuns(project: string): Promise<RunSummary[]> {
  return (await api.get<{ runs: RunSummary[] }>(`${base(project)}/runs`)).data.runs
}

export async function getRun(project: string, runId: string): Promise<RunDetail> {
  return (await api.get<RunDetail>(`${base(project)}/runs/${encodeURIComponent(runId)}`)).data
}

export async function cancelRun(project: string, runId: string): Promise<boolean> {
  return (await api.post(`${base(project)}/runs/${encodeURIComponent(runId)}/cancel`)).data.cancelled
}

export async function deleteRun(project: string, runId: string): Promise<void> {
  await api.delete(`${base(project)}/runs/${encodeURIComponent(runId)}`)
}

export async function getCaseResult(project: string, runId: string, caseId: string): Promise<CaseResult> {
  return (await api.get<CaseResult>(
    `${base(project)}/runs/${encodeURIComponent(runId)}/cases/${encodeURIComponent(caseId)}`)).data
}

/** Evidence file of a run, as a URL for <img>/<a> (cookie-authenticated). */
export function runFileUrl(project: string, runId: string, path: string): string {
  const rel = path.split('/').map(encodeURIComponent).join('/')
  return `${APP_BASE}/api${base(project)}/runs/${encodeURIComponent(runId)}/files/${rel}`
}

/** A JSON evidence file's contents, or null when it does not exist. */
export async function getRunJsonFile<T = unknown>(project: string, runId: string, path: string):
  Promise<T | null> {
  try {
    const rel = path.split('/').map(encodeURIComponent).join('/')
    return (await api.get<T>(`${base(project)}/runs/${encodeURIComponent(runId)}/files/${rel}`)).data
  } catch (err) {
    if ((err as { response?: { status?: number } })?.response?.status === 404) return null
    throw err
  }
}

export async function buildReport(project: string, runId: string): Promise<ReportResult> {
  return (await api.post<ReportResult>(`${base(project)}/runs/${encodeURIComponent(runId)}/report`)).data
}

/** Register a finished run's failures as defects — all, or only `caseIds`. */
export async function syncDefects(project: string, runId: string, caseIds?: string[]):
  Promise<{ created: number; reattached: number; retest_passed: number
    routing?: { sent?: { ref: string; issue: number }[]; waiting?: { ref: string; reasons: string[] }[] } }> {
  return (await api.post(`${base(project)}/runs/${encodeURIComponent(runId)}/sync-defects`,
    caseIds?.length ? { case_ids: caseIds } : {})).data
}

export async function applyTriage(project: string, runId: string):
  Promise<{ applied: number; unmatched: string[]; invalid: number }> {
  return (await api.post(`${base(project)}/runs/${encodeURIComponent(runId)}/apply-triage`)).data
}

export async function listDefects(project: string, filters: {
  status?: DefectStatus
  classification?: DefectClassification
} = {}): Promise<{ defects: Defect[]; counts: Record<DefectStatus, number> }> {
  return (await api.get(`${base(project)}/defects`, { params: filters })).data
}

export async function createDefect(project: string, body: {
  title: string
  description?: string
  severity?: Severity
  case_id?: string | null
  suspected_object?: string | null
}): Promise<Defect> {
  return (await api.post<Defect>(`${base(project)}/defects`, body)).data
}

export async function updateDefect(project: string, id: number,
  fields: Partial<Pick<Defect, 'severity' | 'classification' | 'title' | 'expected' | 'actual'
    | 'repro' | 'suspected_object'>>): Promise<Defect> {
  return (await api.patch<Defect>(`${base(project)}/defects/${id}`, fields)).data
}

export type DefectDecision = 'fix' | 'close'
export type DefectTarget = 'application' | 'test' | 'data'

export interface DefectReview {
  state?: 'auto' | 'needed' | 'decided'
  reasons?: { code: string; text: string }[]
  /** What made it an obvious application error (state "auto"). */
  why?: string[]
  at?: string
  auto_sends?: number
  previous_issue?: number
  decision?: {
    decision: DefectDecision
    target?: DefectTarget | null
    guidance?: string | null
    reason?: string | null
    by?: string
    by_name?: string | null
    at?: string
  }
}

export interface LogResult {
  logged: { ref: string; issue: number }[]
  skipped: { ref: string; reason: string }[]
  failed: { ref: string; error: string }[]
}

export async function logDefects(project: string, body: { ids?: number[]; all_open?: boolean }):
  Promise<LogResult> {
  return (await api.post<LogResult>(`${base(project)}/defects/log`, body)).data
}

/** One line of what happened to a failed test (backend `ui_testing/outcomes.py`). */
export interface StoryStep {
  at: string | null
  kind: string
  title: string
  detail: string
  tone: 'ok' | 'err' | 'warn' | 'info' | 'neutral'
  run?: string | null
  issue?: number | null
}

export interface DefectStory {
  ref: string
  id: number
  case_id: string | null
  title: string
  status: DefectStatus
  headline: string
  tone: StoryStep['tone']
  support: { number: number; status: string; label: string } | null
  steps: StoryStep[]
}

export interface CaseOutcome {
  /** The defect(s) this case failed with in this run. */
  failed: DefectStory[]
  /** For a case that passed: earlier failures that went through Support. */
  fixed_earlier: DefectStory[]
}

export async function getRunOutcomes(project: string, runId: string):
  Promise<{ cases: Record<string, CaseOutcome> }> {
  return (await api.get(`${base(project)}/runs/${encodeURIComponent(runId)}/outcomes`)).data
}

/** A reviewer's decision: fix it (with guidance Support works from) or close it. */
export async function decideDefects(project: string, body: {
  ids: number[]; decision: DefectDecision; target?: DefectTarget; guidance?: string; reason?: string
}): Promise<LogResult | { closed: string[] }> {
  return (await api.post(`${base(project)}/defects/decide`, body)).data
}

export async function closeDefects(project: string, ids: number[], reason: string):
  Promise<{ closed: string[] }> {
  return (await api.post(`${base(project)}/defects/close`, { ids, reason })).data
}

export async function reopenDefects(project: string, ids: number[]): Promise<{ reopened: string[] }> {
  return (await api.post(`${base(project)}/defects/reopen`, { ids })).data
}

export async function raiseIssue(project: string, id: number): Promise<{ ref: string; issue: number }> {
  return (await api.post(`${base(project)}/defects/${id}/raise-issue`)).data
}

/** What the caller may do with a failed case (QC raises tickets; developers
 *  and project managers fix in place). */
export async function getMyAccess(project: string): Promise<{ can_test: boolean; can_fix: boolean }> {
  return (await api.get(`${base(project)}/my-access`)).data
}

/** One failed case → a bug → a Support issue (the quality-control path). */
export async function raiseTicket(project: string, runId: string, caseId: string):
  Promise<{ ref: string; issue: number | null; existing: boolean }> {
  return (await api.post(`${base(project)}/runs/${encodeURIComponent(runId)}/cases/`
    + `${encodeURIComponent(caseId)}/raise-ticket`)).data
}

export async function getTestData(project: string):
  Promise<{ summary: LedgerSummary; records: LedgerEntry[] }> {
  return (await api.get(`${base(project)}/test-data`)).data
}

export async function cleanTestData(project: string, runId: string | null): Promise<CleanResult> {
  return (await api.post<CleanResult>(`${base(project)}/test-data/clean`, { run_id: runId })).data
}

// ── unit tests (the designer's CRUD test, kept per transaction) ──────────

export interface UnitTestRow {
  obj_name: string
  title: string
  folder: string
  /** False = a menu entry with no model yet: listed, never run. */
  built: boolean
  /** The last result; null = never run from here. */
  result: { report: UnitTestReport; at: string; by: string; via?: string } | null
}

export interface UnitTestJob {
  running: boolean
  status: 'running' | 'finished' | 'cancelled' | 'interrupted' | 'error'
  started_at: string
  finished_at?: string
  started_by: string
  objects: string[]
  total: number
  done: number
  current: string | null
  passed: number
  failed: number
  error?: string
}

export async function listUnitTests(project: string):
  Promise<{ objects: UnitTestRow[]; job: UnitTestJob | null }> {
  return (await api.get(`${base(project)}/unit-tests`)).data
}

/** Test these transactions (none = every transaction) in the background. */
export async function runUnitTests(project: string, objects?: string[]): Promise<{ total: number }> {
  return (await api.post(`${base(project)}/unit-tests/run`, { objects: objects || [] })).data
}

export async function cancelUnitTests(project: string): Promise<boolean> {
  return (await api.post(`${base(project)}/unit-tests/cancel`)).data.ok
}

/** The backend's own error text (string detail, or {message, issues}). */
export function apiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] as { msg?: string }
    if (first?.msg) return first.msg
  }
  return fallback
}

/** `detail.issues` of a 422 from saving a case, if any. */
export function apiIssues(err: unknown): Issue[] {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object' && Array.isArray((detail as { issues?: unknown }).issues)) {
    return (detail as { issues: Issue[] }).issues
  }
  return []
}
